import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from 'react-query'
import { toast } from 'sonner'
import { Plus, Pencil, Trash2, MoreHorizontal } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { PageHeader, DataTable } from '@/components/shared'
import { emailSegmentService } from '@/services/emailSegmentService'

const Segments = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [deletingSegment, setDeletingSegment] = useState(null)

  const { data, isLoading } = useQuery('email-segments', emailSegmentService.getAllSegments, {
    onError: () => toast.error('Failed to fetch segments'),
  })
  const segments = data?.data?.segments || []

  const handleDelete = async () => {
    if (!deletingSegment) return
    try {
      await emailSegmentService.deleteSegment(deletingSegment.id)
      toast.success('Segment deleted')
      queryClient.invalidateQueries('email-segments')
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete segment')
    } finally {
      setDeletingSegment(null)
    }
  }

  const columns = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <span className="font-medium text-foreground">{row.original.name}</span>
      ),
    },
    {
      accessorKey: 'description',
      header: 'Description',
      cell: ({ row }) => row.original.description || '—',
    },
    {
      id: 'conditions',
      header: 'Conditions',
      cell: ({ row }) => <Badge variant="secondary">{(row.original.rules || []).length}</Badge>,
    },
    {
      accessorKey: 'last_evaluated_count',
      header: 'Est. Recipients',
      cell: ({ row }) => row.original.last_evaluated_count ?? '—',
    },
    {
      id: 'actions',
      header: '',
      enableSorting: false,
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-40">
            <DropdownMenuItem onClick={() => navigate(`/marketing-email/segments/${row.original.id}/edit`)}>
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={() => setDeletingSegment(row.original)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <PageHeader
        title="Segments"
        description="Group contacts by rules to target campaigns"
        actions={
          <Button size="sm" onClick={() => navigate('/marketing-email/segments/new')}>
            <Plus className="mr-2 h-4 w-4" />
            New Segment
          </Button>
        }
      />

      <DataTable
        columns={columns}
        data={segments}
        loading={isLoading}
        searchPlaceholder="Search segments..."
        emptyMessage="No segments yet. Create one to start targeting campaigns."
        onRowClick={(segment) => navigate(`/marketing-email/segments/${segment.id}`)}
      />

      <AlertDialog open={!!deletingSegment} onOpenChange={() => setDeletingSegment(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Segment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deletingSegment?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export default Segments
